/**
 *  PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 *  Copyright © 2024 Pearson Education, Inc.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 * */

/**
 * @file theme.js - Centralized theme configuration for the Studio module
 * This file contains all color definitions, design tokens, and theme-related constants
 * for consistent styling across the application.
 */

// ==================== COLOR PALETTE ====================

export const colors = {
  // Base colors
  white: '#FFFFFF',
  black: '#000000',

  // Primary brand colors
  primary: {
    purple: '#a78bfa',
    purpleLight: '#c4b5fd',
    purpleDark: '#9333ea',
    purpleExtraLight: '#e9e2fe'
  },

  // Node type colors
  nodeTypes: {
    agent: '#a78bfa',
    supervisor: '#eb6134',
    start: '#4ade80',
    end: '#fb923c',
    tool: '#fcd34d',
    skill: '#38bdf8',
    graph: '#7fa832'
  },

  tracing: {
    hubIcon: '#4ade80',
    surface: {
      container: '#0f1117',
      header: '#151821',
      sidebar: '#10131a',
      sidebarActive: '#1a2030',
      panel: '#0b0f1a',
      leftPanel: '#12151d',
      treeRowSelected: '#1f2433',
      previewSection: '#0a0e17',
      previewSectionHeader: '#101622'
    },
    accent: {
      text: '#c4b5fd',
      bg: 'rgba(167, 139, 250, 0.12)',
      bgMedium: 'rgba(167, 139, 250, 0.15)',
      bgStrong: 'rgba(167, 139, 250, 0.18)',
      bgLight: 'rgba(167, 139, 250, 0.08)'
    },
    overlay: {
      infoBanner: 'rgba(59, 130, 246, 0.06)',
      rowBorder: 'rgba(255,255,255,0.04)',
      rowHover: 'rgba(255,255,255,0.03)',
      rowHoverStrong: 'rgba(255,255,255,0.05)',
      rowSelected: 'rgba(255, 255, 255, 0.04)',
      treeLine: 'rgba(100, 116, 139, 0.4)',
      treeLineBorder: 'rgba(100, 116, 139, 0.35)',
      codeInline: 'rgba(255,255,255,0.06)',
      codeBlock: 'rgba(255,255,255,0.04)',
      codeFaint: 'rgba(255,255,255,0.02)',
      expandHover: 'rgba(255,255,255,0.06)'
    },
    observation: {
      default: '#94a3b8',
      trace: { border: 'rgba(34, 197, 94, 0.35)', color: '#4ade80' },
      generation: { border: 'rgba(244, 114, 182, 0.35)', color: '#f472b6', icon: '#a78bfa' },
      tool: { border: 'rgba(251, 191, 36, 0.35)', color: '#fbbf24', icon: '#fbbf24' },
      agent: { border: 'rgba(52, 211, 153, 0.35)', color: '#34d399', icon: '#34d399' },
      span: { border: 'rgba(96, 165, 250, 0.35)', color: '#60a5fa' }
    },
    timeline: {
      generation: '#8b5cf6',
      tool: '#f59e0b',
      agent: '#10b981',
      default: '#6366f1'
    },
    value: {
      string: '#86efac',
      number: '#60a5fa',
      link: '#7eb8ff'
    },
    debugLevel: '#3b82f6'
  },

  // Background colors
  background: {
    dark: '#1e1e1e',
    medium: '#2d2d2d',
    mediumAlt: '#272727',
    light: '#2a2a2a',
    lighter: '#333',
    lightest: '#3a3a3a',
    overlay: '#00000080',
    overlayDark: '#000000b3',
    overlayLight: '#00000066',
    overlayLighter: '#0000004d',
    whiteOverlay: '#ffffff1a',
    hover: '#4a4a4a',
    hoverAlt: '#5a5a5a',
    hoverLight: '#3a3a3a',
    disabled: '#3a3a3a',
    code: '#272727',
    hover2: '#3d3d3d',
    hover3: '#555'
  },

  // Text colors
  text: {
    primary: '#ffffff',
    secondary: '#e2e8f0',
    tertiary: '#d1d5db',
    quaternary: '#9ca3af',
    dim: '#6b7280',
    muted: '#999',
    placeholder: '#888',
    error: '#f87171',
    link: '#60a5fa',
    linkHover: '#93c5fd',
    codeHighlight: '#fbbf24',
    black: '#020917',
    dark: '#333',
    medium: '#6a7070',
    light: '#c5c5c5'
  },

  // Border colors
  border: {
    default: '#444',
    light: '#3a3a3a',
    lighter: '#ccc',
    lightest: '#e0e0e0',
    medium: '#4f4d4d',
    focus: '#a78bfa',
    hover: '#555',
    hoverAlt: '#666',
    focusAlt: '#007bff',
    dark: '#374151',
    darker: '#4b5563',
    darkest: '#4a5568',
    divider: '#374151',
    conditionalActive: '#a78bfa',
    conditionalInactive: '#4b5563'
  },

  // Status colors
  status: {
    success: '#10b981',
    successLight: '#d4edda',
    successDark: '#155724',
    error: '#ef4444',
    errorLight: '#f8d7da',
    errorDark: '#721c24',
    errorBg: '#fef2f2',
    errorBorder: '#fecaca',
    warning: '#f59e0b',
    warningLight: '#fff3cd',
    warningDark: '#856404',
    info: '#3b82f6',
    infoLight: '#60a5fa',
    active: '#10b981'
  },

  pill: {
    grayBg: '#E2E8F0'
  },

  // Button colors
  button: {
    primary: {
      background: '#a78bfa',
      hover: '#9333ea',
      text: '#000000',
      disabled: '#6b7280',
      disabledBg: '#6b5b95',
      disabledText: '#a0a0a0'
    },
    secondary: {
      background: '#6b7280',
      hover: '#9ca3af',
      hoverAlt: '#5a6268',
      text: '#ffffff',
      border: '#444'
    },
    transparent: {
      background: 'transparent',
      hover: '#3a3a3a',
      hoverAlt: 'rgba(255, 255, 255, 0.08)',
      hoverAlt2: 'rgba(102, 102, 102, 0.08)',
      text: '#ffffff'
    },
    danger: {
      background: '#a78bfa',
      hover: '#c4b5fd',
      text: '#000000'
    },
    gradient: {
      primary: 'linear-gradient(to bottom, #007a8f, #012b5b)',
      secondary: 'linear-gradient(#3e3c44, rgb(63 62 81))'
    },
    success: {
      background: '#10b981',
      hover: '#155724',
      text: '#ffffff'
    },
    error: {
      background: '#ef4444',
      hover: '#721c24',
      text: '#ffffff'
    }
  },

  // Input colors
  input: {
    background: '#333',
    backgroundAlt: 'rgb(40, 40, 40)',
    backgroundDark: '#1e1e1e',
    text: '#e2e8f0',
    border: '#444',
    borderHover: '#555',
    borderFocus: '#a78bfa',
    disabled: '#2a2a2a',
    disabledText: '#888',
    selected: 'rgb(163 163 163)'
  },

  // Selector/Dropdown colors
  selector: {
    background: '#333',
    text: '#ffffff',
    border: '#444',
    borderStyle: '1px solid rgb(111, 111, 111)',
    borderHover: '#666',
    borderFocus: '#007bff',
    icon: '#ffffff',
    label: '#d1d5db',
    labelFocus: '#a78bfa',
    menu: {
      background: '#2d2d2d',
      border: '#444',
      maxHeight: 300
    },
    menuItem: {
      background: '#2d2d2d',
      text: '#ffffff',
      hover: '#3d3d3d',
      selected: '#444',
      selectedHover: '#555'
    }
  },

  // Specific component colors
  sidePanel: {
    background: '#1A202C;',
    buttonColor: '#f8fafc',
    buttonHover: '#4a4a4a',
    conditionalBoxActive: '#333b44',
    conditionalBoxInactive: '#1A202C',
    conditionalEdgeColor: '#f8fafc',
    conditionalEdgeDescription: '#f8fafc',
    conditionalEdgeTip: '#f8fafc',
    disabled: 'rgba(255, 255, 255, 0.5)'
  },

  header: {
    separator: '#888',
    version: '#c5c5c5',
    versionBg: '#3a3f47'
  },

  goDeeperPills: {
    scrollBarTrack: '#F7F9FD',
    scrollBarThumb: 'rgba(0, 0, 0, 0.6)',
    buttonBg: '#FEFEFE',
    buttonHoverBg: '#BCC1CB',
    buttonText: '#020917',
    buttonBorder: '2px solid #C7CCD6',
    titleText: 'inherit'
  },

  /** Dark Trace / Observability preview — configurable GoDeeper colors. */
  goDeeperPillsDark: {
    scrollBarTrack: 'transparent',
    scrollBarThumb: 'rgba(255, 255, 255, 0.35)',
    buttonBg: 'rgba(255, 255, 255, 0.1)',
    buttonHoverBg: 'rgba(255, 255, 255, 0.18)',
    buttonText: '#ffffff',
    buttonBorder: '1px solid rgba(255, 255, 255, 0.35)',
    titleText: '#ffffff'
  },

  graphEditor: {
    popup: '#1e1e1e',
    graphItem: '#2d2d2d',
    select: '#333',
    selectText: '#e2e8f0',
    selectBorder: '#444'
  },

  graphList: {
    background: '#1A202C',
    dropdownBackground: '#252D3A',
    border: '#2d2634',
    borderHover: '#3d3548',
    separator: '#2d3748',
    textPrimary: '#ffffff',
    textSecondary: '#d4d6dd',
    textMuted: '#9ca3af',
    textPlaceholder: '#6b6f7f',
    deleteIcon: '#ef4444',
    deleteIconHover: '#f87171'
  },

  markdown: {
    headingPrimary: '#f3f4f6',
    headingSecondary: '#e5e7eb',
    headingTertiary: '#d1d5db',
    strong: '#f3f4f6',
    code: '#374151',
    codeText: '#fbbf24',
    pre: '#1f2937',
    preCode: '#e5e7eb',
    blockquote: '#9ca3af',
    blockquoteBorder: '#4b5563',
    tableBorder: '#374151',
    tableCellBorder: '#4b5563',
    tableHeader: '#374151',
    tableHeaderText: '#f3f4f6',
    hr: '#374151',
    searchHighlight: '#f59e0b',
    searchHighlightCurrent: '#10b981'
  },

  // Group colors for node grouping
  groupColors: [
    'rgb(224, 231, 255, 0.3)', // #e0e7ff
    'rgb(252, 231, 243, 0.3)', // #fce7f3
    'rgb(254, 249, 195, 0.3)', // #fef9c3
    'rgb(209, 250, 229, 0.3)', // #d1fae5
    'rgb(254, 226, 226, 0.3)', // #fee2e2
    'rgb(224, 242, 254, 0.3)', // #e0f2fe
    'rgb(240, 253, 244, 0.3)' // #f0fdf4
  ],

  groupColorsLight: [
    'rgb(224, 231, 255, 0.9)', // #e0e7ff
    'rgb(252, 231, 243, 0.9)', // #fce7f3
    'rgb(254, 249, 195, 0.9)', // #fef9c3
    'rgb(209, 250, 229, 0.9)', // #d1fae5
    'rgb(254, 226, 226, 0.9)', // #fee2e2
    'rgb(224, 242, 254, 0.9)', // #e0f2fe
    'rgb(240, 253, 244, 0.9)' // #f0fdf4
  ],

  // Special colors
  special: {
    red: '#FF4C4C',
    redHover: '#f44336',
    cyan: '#005d83',
    cyanDark: '#004a6b',
    cyanLight: '#e8f4f8',
    shareEdit: '#10b981',
    shareView: '#f59e0b',
    settingsIcon: '#999',
    loader: '#f3f3f3',
    loaderTop: '#FF4C4C',
    nodeGroup: '#eee',
    nodeGroupBorder: '#cdcdcd',
    nodeGroupText: '#111',
    tableBorder: '#dddddd'
  },

  // Legacy colors (for backward compatibility)
  legacy: {
    lightGray: '#555',
    mediumGray: '#666',
    darkGray: '#888',
    veryDarkGray: '#212529',
    lightGrayText: '#495057',
    mediumGrayText: '#6c757d',
    bgGrayLight: '#f8f9fa',
    bgGrayMedium: '#f5f5f5',
    borderGray: '#dee2e6'
  },

  // History/Diff change type colors
  changeType: {
    add: {
      main: '#61a765',
      dot: '#61a765',
      light: '#e8f5e9',
      border: '#c8e6c9',
      text: '#155724',
      badge: '#d4edda',
      /** Stronger green for inline diff highlights (Improve Prompt modal). */
      textStrong: '#1b5e20'
    },
    update: {
      main: '#f57c00',
      dot: '#ffc107',
      light: '#fff3e0',
      border: '#ffe0b2',
      text: '#856404',
      badge: '#fff3cd'
    },
    delete: {
      main: '#d86363',
      dot: '#d86363',
      light: '#ffebee',
      border: '#ffcdd2',
      text: '#721c24',
      /** Stronger red for inline diff highlights (Improve Prompt modal). */
      textStrong: '#b71c1c'
    },
    default: {
      main: '#1976d2',
      dot: '#6c757d',
      light: '#e3f2fd',
      border: '#90caf9'
    }
  },

  // Edge marker colors
  edge: {
    marker: '#666'
  },

  /** Improve Prompt diff modal (studio graph editor). */
  improvePromptDiff: {
    paneBackground: 'rgba(0, 0, 0, 0.25)'
  },

  /** Graph evaluation criteria side panel. */
  graphEvaluation: {
    panelBackground: '#1A202C',
    fieldBackground: '#1a2030',
    border: '#2d3748',
    borderHover: '#3f4b61',
    closeButtonHover: '#252d3d',
    textPrimary: '#f8fafc',
    textSecondary: 'rgba(248, 250, 252, 0.65)',
    /** Read-only / disabled field text — stays light on dark panel backgrounds */
    textDisabled: '#f8fafc',
    accentText: '#1a1235',
    accentDisabled: 'rgba(167, 139, 250, 0.45)',
    accentDisabledText: 'rgba(26, 18, 53, 0.72)',
    rowHover: '#a78bfa14'
  },

  /** Eval Lens feedback tabs in logs viewer. */
  evalLensFeedback: {
    border: '#3b4252',
    headBackground: '#2b313d',
    rowHover: 'rgba(255, 255, 255, 0.03)'
  }
};

/**
 * Advance Studio scoped CSS custom-property tokens.
 * Values mirror Berlin Studio palette; consumed by buildAdvanceStudioCssVariables().
 */
colors.advanceStudio = {
  accent: colors.primary.purple,
  accentDim: 'rgba(167,139,250,0.16)',
  accentText: colors.primary.purpleLight,
  accentBorder: colors.primary.purpleDark,
  danger: colors.status.error,
  dangerDim: '#3d2020',
  dangerText: colors.text.error,
  warning: colors.status.warning,
  warningDim: '#3d3018',
  warningText: '#f5d9a8',
  inputBackground: colors.input.background,
  codeBackground: colors.background.code,
  border: colors.border.default,
  text: colors.text.primary,
  textSub: colors.text.quaternary,
  label: colors.text.secondary,
  muted: colors.text.dim,
  tool: colors.text.link,
  toolDim: '#1e3a5f',
  hint: '#1a2433',
  hintBorder: '#2a3a52',
  fontFamily: 'system-ui,-apple-system,sans-serif',
  fontMono: "'JetBrains Mono',ui-monospace,monospace",
  fontSize2xs: '11px',
  fontSizeXs: '12px',
  fontSizeSm: '13px',
  fontSizeBase: '14px',
  fontSizeMd: '15px',
  fontSizeLg: '16px',
  fontSizeXl: '18px',
  zoneApp: colors.black,
  zoneNav: colors.sidePanel.background,
  zoneWork: colors.background.dark,
  zoneElev: colors.background.light,
  zoneLog: colors.background.mediumAlt,
  zoneTopbar: colors.background.medium,
  borderNav: colors.border.divider,
  borderWork: colors.border.default,
  borderLog: colors.border.divider,
  statusOkBg: colors.status.success,
  statusOkText: colors.white,
  scrollThumb: 'rgba(255, 255, 255, 0.18)',
  scrollThumbHover: 'rgba(255, 255, 255, 0.28)'
};

// ==================== SHADOWS ====================

export const shadows = {
  small: '0 2px 4px rgba(0,0,0,0.2)',
  medium: '0 4px 6px rgba(0, 0, 0, 0.1)',
  large: '0 4px 20px rgba(0, 0, 0, 0.15)',
  xlarge: '0 8px 32px rgba(0, 0, 0, 0.4)',
  dropdown: '0 4px 6px rgba(0, 0, 0, 0.3)',
  search: '0 4px 12px rgba(0, 0, 0, 0.5)',
  card: '0 8px 32px rgba(0, 0, 0, 0.6)',
  node: `0px 6px 8px 1px rgba(255, 255, 255, 0.05),
         0px 6px 8px 1px rgba(255, 255, 255, 0.13),
         0px 1px 2px 1px rgba(255, 255, 255, 0.2)`
};

// ==================== SPACING ====================

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  xxl: '24px',
  xxxl: '32px'
};

// ==================== BORDER RADIUS ====================

export const borderRadius = {
  small: '3px',
  medium: '4px',
  large: '6px',
  xlarge: '8px',
  xxlarge: '12px',
  round: '50%',
  pill: '20px'
};

// ==================== TYPOGRAPHY ====================

export const typography = {
  fontFamily: {
    primary: 'Roboto, Helvetica, Arial, sans-serif',
    secondary: 'TT Commons',
    mono: "'Courier New', Courier, monospace",
    monoAlt: 'SFMono-Regular, Consolas, Liberation Mono, Menlo, monospace',
    monoUi: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
  },
  fontSize: {
    xs: '11px',
    sm: '12px',
    base: '13px',
    md: '14px',
    lg: '16px',
    xl: '18px',
    xxl: '20px',
    xxxl: '24px'
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700
  },
  lineHeight: {
    tight: '20px',
    normal: '24px',
    relaxed: 1.6,
    loose: 1.7
  }
};

// ==================== TRANSITIONS ====================

export const transitions = {
  fast: '0.15s',
  normal: '0.2s',
  slow: '0.3s',
  verySlow: '0.6s',
  ultra: '0.9s'
};

// ==================== Z-INDEX ====================

export const zIndex = {
  base: 1,
  dropdown: 10,
  sticky: 10,
  fixed: 100,
  modalBackdrop: 999,
  modal: 1000,
  popover: 10000,
  tooltip: 10001,
  max: 10002
};

// ==================== THEME EXPORT ====================

/**
 * Main theme object containing all design tokens
 */
export const theme = {
  colors,
  shadows,
  spacing,
  borderRadius,
  typography,
  transitions,
  zIndex
};

export default theme;

const stripSemicolon = (value) => String(value || '').replace(/;$/, '');

/**
 * CSS custom-property block for Advance Studio (injected under .advance-mode-root).
 * Short var names (--a, --tx, …) match the scoped stylesheet in AdvanceStudioPageStyles.
 */
export const buildAdvanceStudioCssVariables = () => {
  const t = colors.advanceStudio;
  const zoneNav = stripSemicolon(t.zoneNav);

  return `
  --a:${t.accent};
  --ad:${t.accentDim};
  --at:${t.accentText};
  --accent-border:${t.accentBorder};
  --d:${t.danger};
  --dd:${t.dangerDim};
  --dt:${t.dangerText};
  --w:${t.warning};
  --wd:${t.warningDim};
  --wt:${t.warningText};
  --pb:${t.inputBackground};
  --sb:${t.codeBackground};
  --br:${t.border};
  --tx:${t.text};
  --sub:${t.textSub};
  --label:${t.label};
  --muted:${t.muted};
  --mt:var(--sub);
  --tool:${t.tool};
  --tool-d:${t.toolDim};
  --font:${t.fontFamily};
  --mono:${t.fontMono};
  --hint:${t.hint};
  --hint-b:${t.hintBorder};
  --fs-2xs:${t.fontSize2xs};
  --fs-xs:${t.fontSizeXs};
  --fs-sm:${t.fontSizeSm};
  --fs-base:${t.fontSizeBase};
  --fs-md:${t.fontSizeMd};
  --fs-lg:${t.fontSizeLg};
  --fs-xl:${t.fontSizeXl};
  --zone-app:${t.zoneApp};
  --zone-nav:${zoneNav};
  --zone-graphs:${zoneNav};
  --zone-nav-body:${zoneNav};
  --zone-work:${t.zoneWork};
  --zone-elev:${t.zoneElev};
  --zone-log:${t.zoneLog};
  --zone-chat:${zoneNav};
  --zone-topbar:${t.zoneTopbar};
  --br-nav:${t.borderNav};
  --br-work:${t.borderWork};
  --br-log:${t.borderLog};
  --br-chat:${t.borderLog};
  --status-ok-bg:${t.statusOkBg};
  --status-ok-text:${t.statusOkText};
  --scroll-thumb:${t.scrollThumb};
  --scroll-thumb-hover:${t.scrollThumbHover};
  `.trim();
};

// Export commonly used values for easier access

export const LIGHT_PURPLE = colors.primary.purple;

export const STUDIO_APP_COLOR = '#1A202C';

// Group colors for node grouping
export const { groupColors, groupColorsLight } = colors;
